import {
  DOUBLE_ESCAPE_TIMEOUT_MS,
  GO_SEQUENCE_TIMEOUT_MS,
  goToTargetForKey,
  OVERLAY_DISMISS_COOLDOWN_MS,
  type GoToTarget,
} from "@components/keyboardShortcuts";
import { getOpenDismissableCount } from "@scribenote/brian-design-system";
import { useCallback, useEffect, useEffectEvent, useRef } from "react";

interface CompanionShortcutHandlers {
  /** Open the subject/view search (⌘/Ctrl K). */
  onSearch: () => void;
  /** Open the keyboard-shortcuts cheat sheet (?). */
  onHelp: () => void;
  /** Run a `G`-sequence go-to (e.g. `G H`, `G T`). */
  onGoTo: (target: GoToTarget) => void;
  /** Switch user — triggered by Esc Esc when nothing is open to dismiss. */
  onSwitchUser: () => void;
}

/** True when focus sits in a field where typed letters must pass through. */
const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable
  );
};

/**
 * True when an overlay is open that owns Escape (dismisses on its own).
 *
 * INVARIANT: every Escape-consuming overlay in the app must be countable here,
 * or dismissing it would wrongly count toward Esc-Esc and sign the user out.
 * Kit overlays hold that up on their own - `useDialog` and `usePopover` count
 * themselves while open, so anything built on the kit (Sheet, Clipboard,
 * ConfirmDialog, Popover, Dropdown, Select) is counted with no opt-in and none
 * can be forgotten. The exception is a "manual" popover, which the kit
 * deliberately leaves uncounted because it consumes nothing: Tooltip is up on
 * hover, Escape passes through it, so counting it would kill Esc-Esc for as
 * long as a tooltip showed. Only an overlay the kit does not drive - or a
 * manual popover whose host does own Escape - has to say so, by calling the
 * kit's `registerDismissable` while it is open the way `useDismissable` does.
 *
 * The count is read synchronously here because it is still up during the Escape
 * that closes the overlay: the dialog's cancel/close and the popover's light
 * dismiss are default actions the browser runs only after this listener, and
 * React flushes the resulting close later still.
 */
const hasSelfDismissingOverlay = (): boolean => getOpenDismissableCount() > 0;

/**
 * Document-level handler for Companion's global shortcuts. Mounted once from
 * CompanionControls, so it runs on every authenticated route but never on the
 * access-code screen.
 *
 * ⌘/Ctrl K opens Search regardless of focus. `?` opens the cheat sheet; `G` then a
 * letter navigates (GO_TO_SHORTCUTS) within a short armed window, disarming on an
 * unrecognised key or timeout. Both are ignored while typing, on held keys, and on
 * Ctrl/Alt/Meta chords (Shift is allowed, since `?` needs it).
 *
 * Escape is staggered: one Escape aborts a pending `G` or lets the topmost
 * overlay dismiss itself. Only when nothing is open does Escape count toward
 * Esc-Esc, which switches user on the second press — so we never sign someone
 * out while they were merely closing something.
 */
export const useCompanionShortcuts = ({
  onSearch,
  onHelp,
  onGoTo,
  onSwitchUser,
}: CompanionShortcutHandlers): void => {
  const goPendingRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);
  // Timestamp of the last lone Escape (nothing open). A second Escape within
  // DOUBLE_ESCAPE_TIMEOUT_MS of it switches user; 0 means no buffered Escape.
  const lastEscRef = useRef(0);
  // Timestamp of the last Escape that dismissed an overlay, so a lone Escape in
  // its cooldown can't arm an Esc-Esc (guards against mashing Escape to close
  // a stack of overlays and accidentally signing out).
  const lastOverlayDismissRef = useRef(0);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // An effect event always sees the latest handlers, so the listener below can
  // subscribe only once without going stale.
  const handleKeyDown = useEffectEvent((e: KeyboardEvent) => {
    const disarm = () => {
      clearTimer();
      goPendingRef.current = false;
    };
    const resetEscBuffer = () => {
      lastEscRef.current = 0;
    };

    // ⌘/Ctrl K — Search. Fires regardless of focus, matching prior behavior.
    if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      disarm();
      resetEscBuffer();
      onSearch();
      return;
    }

    if (e.key === "Escape") {
      // Ignore a held Escape so it can't auto-repeat into a switch-user.
      if (e.repeat) return;

      // A pending `G` takes priority: Escape aborts the half-typed sequence.
      if (goPendingRef.current) {
        disarm();
        resetEscBuffer();
        return;
      }

      // An open overlay owns this Escape: it self-dismisses, so the press must
      // not count toward switching user, it breaks any buffered double-press,
      // and it starts a cooldown so the next lone Escape can't immediately arm
      // an Esc-Esc.
      if (hasSelfDismissingOverlay()) {
        lastOverlayDismissRef.current = Date.now();
        resetEscBuffer();
        return;
      }

      // Nothing to dismiss. Escape in a field, or with a modifier held, never
      // switches user.
      if (
        isEditableTarget(e.target) ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey ||
        e.shiftKey
      ) {
        resetEscBuffer();
        return;
      }

      const now = Date.now();

      // Within the cooldown of an overlay-dismissing Escape, a lone Escape is
      // almost certainly the tail of the same "close things" mash, not a
      // deliberate switch-user — ignore it rather than arming the pair. Roll the
      // window forward so a sustained mash keeps resetting it; switching user
      // then requires a real pause before the deliberate Esc-Esc.
      if (now - lastOverlayDismissRef.current < OVERLAY_DISMISS_COOLDOWN_MS) {
        lastOverlayDismissRef.current = now;
        resetEscBuffer();
        return;
      }

      // Count toward Esc-Esc; a second press within the window switches user.
      if (now - lastEscRef.current < DOUBLE_ESCAPE_TIMEOUT_MS) {
        lastEscRef.current = 0;
        onSwitchUser();
        return;
      }
      lastEscRef.current = now;
      return;
    }

    // The sequence and `?` shortcuts ignore held keys and Ctrl/Alt/Meta chords.
    // Shift stays allowed because `?` is Shift + `/`.
    if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;

    // Never intercept keys typed into a field.
    if (isEditableTarget(e.target)) return;

    // Any non-Escape key breaks a buffered Escape — Esc-Esc must be consecutive.
    resetEscBuffer();

    if (goPendingRef.current) {
      disarm();
      const target = goToTargetForKey(e.key);
      if (target) {
        e.preventDefault();
        onGoTo(target);
      }
      return;
    }

    if (e.key.toLowerCase() === "g") {
      e.preventDefault();
      goPendingRef.current = true;
      clearTimer();
      timeoutRef.current = window.setTimeout(() => {
        goPendingRef.current = false;
        timeoutRef.current = null;
      }, GO_SEQUENCE_TIMEOUT_MS);
      return;
    }

    if (e.key === "?") {
      e.preventDefault();
      onHelp();
    }
  });

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => handleKeyDown(e);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      clearTimer();
    };
  }, [clearTimer]);
};
